/**
 * @file PopulationGroupDownloadMenu.js
 * @description Download control offering CSV or Excel export of every individual within a population group.
 */

import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styles from './PopulationGroup.module.scss';
import Toast from '../ui/toasts/Toast';
import { fetchAllPopulationGroupIndividuals } from '../../services/populationGroupService';
import { exportCsvReport } from '../../utils/reporting/csvReportExporter';
import { exportExcelReport } from '../../utils/reporting/excelReportExporter';
import { getText } from '../../i18n';

const DEFAULT_SORT = 'nameasc';

function sanitizeFilenamePart(value) {
    return String(value || 'population_group')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
}

function buildIndividualsReportDefinition(populationGroup, sort) {
    return {
        headers: [
            getText('msg_175b3d474db1'),
            getText('msg_ac0e638191f6'),
            getText('msg_120ce31ac668'),
        ],
        filenameBuilder: dateStamp =>
            `${sanitizeFilenamePart(populationGroup?.name)}_individuals_${dateStamp}.csv`,
        getData: () =>
            fetchAllPopulationGroupIndividuals(
                populationGroup?.id,
                '',
                sort || DEFAULT_SORT,
            ),
        mapRow: individual => ({
            uniqueId: individual?.uniqueId || '',
            lastName: individual?.lastName || '',
            firstName: individual?.firstName || '',
        }),
    };
}

function PopulationGroupDownloadMenu({ populationGroup, variant, sort }) {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
        function handleOutsideClick(event) {
            if (
                containerRef.current &&
                !containerRef.current.contains(event.target)
            ) {
                setIsMenuOpen(false);
            }
        }

        document.addEventListener('mousedown', handleOutsideClick);
        return () =>
            document.removeEventListener('mousedown', handleOutsideClick);
    }, []);

    function toggleMenu() {
        if (isDownloading) return;
        setIsMenuOpen(previousIsMenuOpen => !previousIsMenuOpen);
    }

    async function downloadIndividuals(exportReport) {
        setIsMenuOpen(false);
        setIsDownloading(true);

        try {
            await exportReport(
                buildIndividualsReportDefinition(populationGroup, sort),
            );
        } catch (error) {
            console.error(
                'Error downloading population group individuals:',
                error,
            );
            Toast.error(getText('msg_a296e8936da6'));
        } finally {
            setIsDownloading(false);
        }
    }

    const downloadLabel = getText('msg_a479c9c34e87');
    const iconClassName = isDownloading
        ? 'bi bi-hourglass-split'
        : 'bi bi-cloud-download';

    return (
        <div className={styles.downloadMenuContainer} ref={containerRef}>
            {variant === 'text' ? (
                <button
                    type="button"
                    className="ing-text-buttons border-0 bg-transparent"
                    onClick={toggleMenu}
                    disabled={isDownloading}
                >
                    <i className={`${iconClassName} me-2`} />
                    {downloadLabel}
                </button>
            ) : (
                <button
                    type="button"
                    className="p-0 circularIconButton"
                    aria-label={downloadLabel}
                    onClick={toggleMenu}
                    disabled={isDownloading}
                >
                    <i className={iconClassName} />
                </button>
            )}
            {isMenuOpen && (
                <div className={styles.downloadMenu}>
                    <button
                        type="button"
                        className={styles.downloadMenuItem}
                        onClick={() => downloadIndividuals(exportCsvReport)}
                    >
                        {getText('msg_3281188351b8')}
                    </button>
                    <button
                        type="button"
                        className={styles.downloadMenuItem}
                        onClick={() => downloadIndividuals(exportExcelReport)}
                    >
                        {getText('msg_a08e11119aef')}
                    </button>
                </div>
            )}
        </div>
    );
}

PopulationGroupDownloadMenu.propTypes = {
    populationGroup: PropTypes.object.isRequired,
    variant: PropTypes.oneOf(['icon', 'text']),
    sort: PropTypes.string,
};

PopulationGroupDownloadMenu.defaultProps = {
    variant: 'icon',
    sort: DEFAULT_SORT,
};

export default PopulationGroupDownloadMenu;
